import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAuth from '../../../hooks/useAuth';

// ─────────────────────────────────────────────────────────────────
// Glass-i — useErpGridPersistence
//
// Persists per-user, per-grid UI preferences in localStorage. The
// storage key is bound to the logged-in tenant + user so two users
// sharing a workstation never see each other's column layout.
//
// Scope (`GridPersistedState` — see gridTypes.js):
//   hiddenColumnKeys, columnOrder, columnWidths, pageSize, density,
//   sort, filters, search
//
// HARD RULES:
//   1. Never store row data. UI prefs only.
//   2. Never store credentials, tokens, or anything tenant-sensitive.
//   3. A malformed JSON blob in storage is silently dropped — UX
//      always falls back to defaults rather than crashing.
//   4. Anonymous callers (no `me.userId`) get an in-memory-only state
//      — nothing persists. Avoids leaking prefs across logout.
//
// Storage key: glassi:grid:{tenantId|anon}:{userId|anon}:{gridId}
// ─────────────────────────────────────────────────────────────────

/** @typedef {import('./gridTypes').GridPersistedState} GridPersistedState */

const STORAGE_PREFIX = 'glassi:grid';

function buildStorageKey({ tenantId, userId, gridId }) {
  const t = tenantId || 'anon';
  const u = userId || 'anon';
  return `${STORAGE_PREFIX}:${t}:${u}:${gridId}`;
}

function safeReadJson(key) {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
}

function safeWriteJson(key, value) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    // Quota exceeded, private mode, etc. — silently drop. The grid is
    // fully functional without persistence; degrading is acceptable.
  }
}

function safeRemove(key) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.removeItem(key);
  } catch (_e) {
    /* noop */
  }
}

/**
 * Merge a partial update into the persisted state, then write back.
 *
 * @param {GridPersistedState|null} prev
 * @param {Partial<GridPersistedState>} patch
 * @returns {GridPersistedState}
 */
function applyPatch(prev, patch) {
  return { ...(prev || {}), ...patch };
}

/**
 * @param {{gridId:string, defaults?:GridPersistedState}} opts
 * @returns {{
 *   state: GridPersistedState,
 *   ready: boolean,
 *   setPersisted: (patch:Partial<GridPersistedState>) => void,
 *   reset: () => void,
 *   storageKey: string|null
 * }}
 */
export default function useErpGridPersistence({ gridId, defaults }) {
  const { me } = useAuth();
  const tenantId = me?.tenantId;
  const userId = me?.userId || me?.id;

  // Once-per-mount key. We deliberately want the key to change if the
  // identity changes (so we read the right slot), so memoise on the
  // identity tuple.
  const storageKey = useMemo(() => {
    if (!gridId) return null;
    return buildStorageKey({ tenantId, userId, gridId });
  }, [tenantId, userId, gridId]);

  // Lazy initial read so SSR / no-window environments don't crash.
  const [state, setState] = useState(() => {
    if (!storageKey) return defaults || {};
    const persisted = safeReadJson(storageKey);
    return persisted || defaults || {};
  });

  // If the storage key changes (e.g., user logs in/out within the
  // same page lifecycle), re-read from the new slot.
  const lastKeyRef = useRef(storageKey);
  useEffect(() => {
    if (lastKeyRef.current === storageKey) return;
    lastKeyRef.current = storageKey;
    if (!storageKey) {
      setState(defaults || {});
      return;
    }
    const persisted = safeReadJson(storageKey);
    setState(persisted || defaults || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const setPersisted = useCallback(
    (patch) => {
      setState((prev) => {
        const next = applyPatch(prev, patch || {});
        if (storageKey) safeWriteJson(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const reset = useCallback(() => {
    if (storageKey) safeRemove(storageKey);
    setState(defaults || {});
  }, [storageKey, defaults]);

  return {
    state,
    ready: Boolean(storageKey),
    setPersisted,
    reset,
    storageKey
  };
}
